import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle, XCircle, Loader2, RefreshCw, School, Link2Off, AlertTriangle, Clock, Activity, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const relativeTime = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 'never';
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const WondeIntegration = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const pollRef = React.useRef(null);

  // Setup wizard state
  const [showSetup, setShowSetup] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [tokenInput, setTokenInput] = useState('');
  const [schools, setSchools] = useState([]);
  const [fetchingSchools, setFetchingSchools] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [connecting, setConnecting] = useState(false);
  // Manual school ID fallback (when token lacks schools.read permission)
  const [manualMode, setManualMode] = useState(false);
  const [manualSchoolId, setManualSchoolId] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/wonde/status');
      setStatus(res.data);
      // If we were syncing and it's now completed, stop polling
      if (res.data.sync_status !== 'running') {
        setSyncing(false);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch (err) {
      if (err.response?.status !== 403) {
        console.error('Failed to fetch Wonde status:', err);
      }
      setStatus({ wonde_enabled: false, connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  // Resume polling if page loads with sync already running
  useEffect(() => {
    fetchStatus().then(() => {
      // status set asynchronously, check via API response directly
    });
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.sync_status === 'running' && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 4000);
    }
    return () => {
      if (pollRef.current && status?.sync_status !== 'running') {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [status?.sync_status, fetchStatus]);

  const handleFetchSchools = async () => {
    if (!tokenInput.trim()) {
      toast.error('Please enter your Wonde API token');
      return;
    }
    setFetchingSchools(true);
    try {
      const res = await api.get('/wonde/schools', { params: { token: tokenInput.trim() } });
      const schoolList = res.data.schools || [];
      if (!schoolList.length) {
        toast.error('No schools found for this token');
        return;
      }
      setSchools(schoolList);
      setSelectedSchoolId(schoolList[0].id);
      setWizardStep(2);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // If the token lacks schools.read permission, offer manual ID entry
      if (err.response?.status === 400 && detail.toLowerCase().includes('permission')) {
        setManualMode(true);
        setWizardStep(2);
      } else {
        toast.error(detail || 'Failed to fetch schools. Check your token.');
      }
    } finally {
      setFetchingSchools(false);
    }
  };

  const handleConnect = async () => {
    // Manual mode: use the typed school ID directly
    const schoolId = manualMode ? manualSchoolId.trim() : selectedSchoolId;
    if (!schoolId) {
      toast.error('Please enter your School ID');
      return;
    }
    setConnecting(true);
    try {
      // school_name is optional — backend will auto-resolve it from Wonde API
      const payload = manualMode
        ? { token: tokenInput.trim(), school_id: schoolId }
        : { token: tokenInput.trim(), school_id: schoolId, school_name: (schools.find(s => s.id === schoolId) || {}).name };
      const res = await api.post('/wonde/connect', payload);
      toast.success(res.data.message || 'School connected');
      setShowSetup(false);
      setWizardStep(1);
      setTokenInput('');
      setSchools([]);
      setManualMode(false);
      setManualSchoolId('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      await api.post('/wonde/sync');
      toast.success('Sync started — results will appear here shortly');
      // Start polling every 4 seconds
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchStatus, 4000);
      fetchStatus();
    } catch (err) {
      setSyncing(false);
      toast.error(err.response?.data?.detail || 'Sync failed. Please try again.');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Wonde? Existing synced subscribers will not be removed.')) return;
    setDisconnecting(true);
    try {
      await api.delete('/wonde/disconnect');
      toast.success('Wonde disconnected');
      setSyncResult(null);
      fetchStatus();
    } catch (err) {
      toast.error('Failed to disconnect Wonde');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-slate-400">
        <Loader2 size={16} className="animate-spin" />
        <span className="text-sm">Loading Wonde status...</span>
      </div>
    );
  }

  // Feature not enabled by super admin
  if (!status?.wonde_enabled) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5" data-testid="wonde-not-enabled">
        <div className="flex items-center gap-3 mb-2">
          <School size={20} className="text-amber-600" />
          <span className="font-semibold text-slate-800">Wonde MIS Integration</span>
        </div>
        <p className="text-sm text-slate-600">
          Sync parent and guardian contacts from your school MIS system directly into your subscriber lists.
        </p>
        <p className="text-sm text-amber-700 mt-2">
          This feature is not yet enabled for your account. Contact your NexusCMS administrator to enable Wonde sync.
        </p>
      </div>
    );
  }

  // Setup wizard (not yet connected)
  if (!status?.connected) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-5" data-testid="wonde-setup">
        <div className="flex items-center gap-3 mb-1">
          <School size={20} className="text-green-700" />
          <span className="font-semibold text-slate-800">Wonde MIS Integration</span>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Connect your Wonde account to automatically sync parent/guardian contacts into subscriber groups by year.
        </p>

        {!showSetup ? (
          <Button onClick={() => setShowSetup(true)} variant="outline" data-testid="wonde-setup-btn">
            Set Up Wonde
          </Button>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4 shadow-sm">
            {/* Step indicator */}
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className={`px-2 py-0.5 rounded-full ${wizardStep === 1 ? 'bg-green-100 text-green-700 font-medium' : 'bg-slate-100'}`}>1 Enter Token</span>
              <span className="text-slate-300">→</span>
              <span className={`px-2 py-0.5 rounded-full ${wizardStep === 2 ? 'bg-green-100 text-green-700 font-medium' : 'bg-slate-100'}`}>2 Select School</span>
              <span className="text-slate-300">→</span>
              <span className="px-2 py-0.5 rounded-full bg-slate-100">3 Connect</span>
            </div>

            {wizardStep === 1 && (
              <>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Wonde API Token</Label>
                  <Input
                    type="password"
                    placeholder="Paste your Wonde school token"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    data-testid="wonde-token-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Find this in your Wonde dashboard under Settings → API Tokens.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleFetchSchools}
                    disabled={fetchingSchools || !tokenInput.trim()}
                    data-testid="wonde-fetch-schools-btn"
                  >
                    {fetchingSchools
                      ? <><Loader2 size={14} className="animate-spin mr-2" />Fetching Schools...</>
                      : 'Fetch Schools'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowSetup(false); setTokenInput(''); }}>Cancel</Button>
                </div>
              </>
            )}

            {wizardStep === 2 && (
              <>
                {manualMode ? (
                  /* Manual School ID entry (token lacks schools.read permission) */
                  <div>
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 mb-3 text-xs text-amber-800">
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                      <span>
                        Your token doesn't have permission to list schools automatically. Enter your School ID manually instead.
                        Find it in your <strong>Wonde dashboard → Settings → School Details</strong> (looks like <code className="font-mono bg-amber-100 px-1 rounded">A1234567890</code>).
                      </span>
                    </div>
                    <Label className="text-xs text-slate-500 mb-1 block">School ID</Label>
                    <Input
                      placeholder="e.g. A1234567890"
                      value={manualSchoolId}
                      onChange={(e) => setManualSchoolId(e.target.value)}
                      data-testid="wonde-manual-school-id-input"
                    />
                  </div>
                ) : (
                  /* Dropdown from fetched schools list */
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Select School</Label>
                    <Select value={selectedSchoolId} onValueChange={setSelectedSchoolId}>
                      <SelectTrigger data-testid="wonde-school-select">
                        <SelectValue placeholder="Choose a school" />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}{s.town ? ` — ${s.town}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400 mt-1">
                      {schools.length} school{schools.length !== 1 ? 's' : ''} found for your token.
                    </p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={handleConnect}
                    disabled={connecting || (manualMode ? !manualSchoolId.trim() : !selectedSchoolId)}
                    data-testid="wonde-connect-btn"
                  >
                    {connecting
                      ? <><Loader2 size={14} className="animate-spin mr-2" />Connecting...</>
                      : 'Connect School'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setWizardStep(1); setManualMode(false); setManualSchoolId(''); }}>Back</Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Connected state
  const lastResult = status.last_sync_result;
  const hasError = lastResult && (lastResult.errors > 0 || lastResult.error);
  const neverSynced = !status.last_sync_at;

  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-5" data-testid="wonde-connected">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <CheckCircle size={18} className="text-green-600" />
            <span className="font-semibold text-slate-800">Wonde MIS Integration</span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{status.school_name}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDisconnect}
          disabled={disconnecting}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid="wonde-disconnect-btn"
        >
          {disconnecting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Link2Off size={14} className="mr-1" />}
          Disconnect
        </Button>
      </div>

      {/* Sync Health */}
      <div className="bg-white/70 rounded-lg border border-slate-200 p-4 mb-3" data-testid="wonde-sync-health">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-green-600" />
            <span className="text-sm font-semibold text-slate-800">Sync Health</span>
          </div>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
            status?.sync_status === 'running' ? 'bg-blue-100 text-blue-700' :
            neverSynced ? 'bg-slate-100 text-slate-600' :
            hasError ? 'bg-red-100 text-red-700' :
            'bg-green-100 text-green-700'
          }`} data-testid="wonde-sync-status">
            {status?.sync_status === 'running' ? <><Loader2 size={12} className="animate-spin" /> Syncing...</> :
             neverSynced ? <><Clock size={12} /> Never synced</> :
             hasError ? <><AlertTriangle size={12} /> Needs attention</> :
             <><CheckCircle size={12} /> Healthy</>}
          </span>
        </div>

        {neverSynced ? (
          <p className="text-sm text-slate-600">
            No sync has run yet. Click "Sync Now" to import parent contacts from Wonde.
          </p>
        ) : (
          <div className="text-sm text-slate-600 mb-2">
            Last sync <span className="font-medium text-slate-800">{relativeTime(status.last_sync_at)}</span>
            <span className="block text-xs text-slate-400 mt-0.5">{new Date(status.last_sync_at).toLocaleString()}</span>
          </div>
        )}

        {lastResult && !lastResult.note && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            <div className="rounded-md bg-green-50 border border-green-100 px-2 py-2 text-center" data-testid="wonde-stat-created">
              <div className="text-lg font-semibold text-green-700">{lastResult.created ?? 0}</div>
              <div className="text-xs text-slate-500">New</div>
            </div>
            <div className="rounded-md bg-blue-50 border border-blue-100 px-2 py-2 text-center" data-testid="wonde-stat-updated">
              <div className="text-lg font-semibold text-blue-700">{lastResult.updated ?? 0}</div>
              <div className="text-xs text-slate-500">Updated</div>
            </div>
            <div className="rounded-md bg-indigo-50 border border-indigo-100 px-2 py-2 text-center" data-testid="wonde-stat-groups">
              <div className="text-lg font-semibold text-indigo-700">{lastResult.year_groups_created ?? 0}</div>
              <div className="text-xs text-slate-500">New Groups</div>
            </div>
            <div className={`rounded-md px-2 py-2 text-center border ${(lastResult.errors ?? 0) > 0 ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`} data-testid="wonde-stat-errors">
              <div className={`text-lg font-semibold ${(lastResult.errors ?? 0) > 0 ? 'text-red-700' : 'text-slate-400'}`}>{lastResult.errors ?? 0}</div>
              <div className="text-xs text-slate-500">Errors</div>
            </div>
          </div>
        )}

        {lastResult?.note && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-2 mt-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{lastResult.note}</span>
          </div>
        )}

        {lastResult?.year_groups && lastResult.year_groups.length > 0 && (
          <div className="flex items-start gap-2 mt-2 pt-2 border-t border-slate-100">
            <Users size={13} className="text-indigo-500 mt-0.5 flex-shrink-0" />
            <div className="flex flex-wrap gap-1">
              {lastResult.year_groups.map(yg => (
                <span key={yg} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-100">{yg}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sync Action */}
      <Button
        onClick={handleSync}
        disabled={syncing || status?.sync_status === 'running'}
        data-testid="wonde-sync-btn"
      >
        {(syncing || status?.sync_status === 'running')
          ? <><Loader2 size={14} className="animate-spin mr-2" />Syncing — please wait...</>
          : <><RefreshCw size={14} className="mr-2" />Sync Now</>}
      </Button>

      {/* Sync result toast confirmation */}
      {syncResult?.result?.note && (
        <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-3" data-testid="wonde-sync-result">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{syncResult.result.note}</span>
        </div>
      )}
    </div>
  );
};

export default WondeIntegration;
